import { CyberpassButton, CyberpassInput } from "../Input";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as locationApi from "../api/location";
import * as userApi from "../api/user";
import Modal from "../Modal";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { selectUser, updateAccessToken } from "../store/auth";
import { clearRecent, clearCurrent } from "../store/customer";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../i18n";

/**
 * @returns {React.Component}
 */
export default function Login() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  /** @type {[Array<ManagerLocation>, React.Dispatch<Array<ManagerLocation>>]} */
  const [locations, setLocations] = useState([]);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [showRestorePasswordModal, setShowRestorePasswordModal] =
    useState(false);
  const [location, setLocation] = useLocalStorage("lastLocation", null);
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!user) {
      return;
    }
    const redirectTo = searchParams.get("redirect_to");
    if (redirectTo) {
      navigate(redirectTo);
      return;
    }
    if (user.role === 1) navigate("/dashboard/admin");
    if (user.role === 2) navigate("/dashboard/super");
  }, [user, navigate, searchParams]);

  useEffect(() => {
    locationApi
      .fetchActive()
      .then((locations) => {
        if (location == null) {
          setLocations(locations);
          return;
        }

        setLocations([
          location,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          ...locations.filter((loc) => loc.id != location.id),
        ]);
      })
      .catch((err) => {
        console.error("Failed to fetch locations list with", err);
        toast.error(t("auth.locationsError"));
      });
  }, []);

  /**
   * @param {React.FormEvent} e
   */
  const handleLogin = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    console.log("Login form data location id");
    const locationId = Number(formData.get("locationid"));
    setLocation(locations.find((loc) => loc.id == locationId));

    const resp = await userApi.login(formData);

    if (resp.status == 200) {
      const token = await resp.json();
      localStorage.setItem("userCache", "");
      dispatch(clearCurrent());
      dispatch(clearRecent());
      dispatch(updateAccessToken(token));
    } else {
      try {
        const { detail } = await resp.json();
        toast.error(
          detail === "Incorrect username or password"
            ? t("auth.incorrectLoginPassword")
            : detail === "Provided location not found"
              ? t("auth.locationDeleted")
              : t("auth.incorrectCredentials"),
        );
      } catch (e) {
        console.error(e);
        toast.error(t("auth.unexpectedError"));
      }

      console.error(await resp.text());
    }
  };

  return (
    <div className="flex items-center justify-center h-screen">
      <form
        onSubmit={handleLogin}
        className="flex flex-col space-y-5"
        autoCapitalize="none"
      >
        <CyberpassInput
          type="text"
          name="username"
          id="username"
          label={t("auth.login")}
          placeholder="my.username"
          required={true}
        />
        <CyberpassInput
          type="password"
          name="password"
          id="password"
          label={t("auth.password")}
          placeholder="qwerty1234"
          required={true}
        />
        <select
          name="locationid"
          id="locationid"
          className="bg-c-gray-600 p-1 rounded"
        >
          {locations.map((loc, idx) => (
            <option value={loc.id} key={idx} className="border-none ring-0">
              {loc.name}
            </option>
          ))}
        </select>
        <div className="flex flex-col mx-auto justify-center space-y-2">
          <span className="h-8" />
          <CyberpassButton type="submit">
            <div className="flex flex-row items-center justify-center">
              <svg
                width="17"
                height="18"
                viewBox="0 0 17 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.45455 6.2729V2.86381C6.45455 1.73414 7.37033 0.81836 8.5 0.81836L13.9545 0.818359C15.0842 0.818359 16 1.73414 16 2.86381L16 15.1365C16 16.2662 15.0842 17.182 13.9545 17.182H8.5C7.37033 17.182 6.45455 16.2662 6.45455 15.1365V11.7274M9.86364 6.27291L12.5909 9.00018M12.5909 9.00018L9.86364 11.7274M12.5909 9.00018L1 9.00018"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="w-2" />
              {t("auth.signIn")}
            </div>
          </CyberpassButton>
          <a
            className="flex mx-auto hover:underline cursor-pointer"
            onClick={() => setShowRestorePasswordModal(true)}
          >
            {t("auth.restorePassword")}
          </a>
        </div>
      </form>
      <Modal
        title={t("auth.passwordRecovery")}
        content={
          <CyberpassInput
            label={t("auth.telegramUsername")}
            type="text"
            id="tg_username"
            name="tg_username"
            value={telegramUsername}
            onChange={(e) => setTelegramUsername(e.target.value)}
          />
        }
        showModal={showRestorePasswordModal}
        setShowModal={setShowRestorePasswordModal}
        setModalConfirmed={() => {
          toast.promise(userApi.restorePassword({ telegramUsername }), {
            success: t("auth.passwordSent"),
            error: t("auth.userNotFoundInTelegram"),
          });
        }}
      />
    </div>
  );
}
